import * as React from "react";
import { Customer } from "./team.types";

interface Props {
  options: Customer[];        // full list to choose from
  selectedIds: string[];      // array of _id
  onChange: (ids: string[]) => void;
  variant?: "leader" | "member";
  placeholder?: string;
}

export default function PeopleMultiSelect({
  options,
  selectedIds,
  onChange,
  variant = "member",
  placeholder = "Search people…",
}: Props) {
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const byId = React.useMemo(() => {
    const m: Record<string, Customer> = {};
    options.forEach((o) => (m[o._id] = o));
    return m;
  }, [options]);

  const selected = selectedIds.map((id) => byId[id]).filter(Boolean);

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;

  const toggle = (id: string) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    onChange(next);
    setQuery(""); // clear search after each pick (mockup behavior)
  };

  const remove = (id: string) => onChange(selectedIds.filter((x) => x !== id));

  return (
    <div className="ctpl-ms-field" ref={wrapRef}>
      <div className="ctpl-ms-control" onClick={() => setOpen(true)}>
        {selected.map((s) => (
          <span key={s._id} className={`ctpl-ms-tag${variant === "member" ? " member" : ""}`}>
            {s.name}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                remove(s._id);
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="ctpl-ms-input"
          placeholder={selected.length ? "" : placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
      </div>

      {open && (
        <div className="ctpl-ms-dropdown">
          {filtered.length === 0 ? (
            <div className="ctpl-ms-empty">No matching people</div>
          ) : (
            filtered.map((o) => {
              const isSel = selectedIds.includes(o._id);
              return (
                <div
                  key={o._id}
                  className={`ctpl-ms-option${isSel ? " selected" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(o._id);
                  }}
                >
                  <input type="checkbox" readOnly checked={isSel} />
                  <span>{o.name}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}